import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  CircularProgress
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const API_URL = process.env.REACT_APP_API_URL;

export default function Results() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const res = await fetch((`${API_URL}/all-results`));
      const data = await res.json();

      setGames(data);
    } catch (err) {
      console.error("Failed to fetch results:", err);
    } finally {
      setLoading(false);
    }
  };

  // Group games by round
  const groupedGames = games.reduce((acc, game) => {
    if (!acc[game.round_number]) {
      acc[game.round_number] = [];
    }

    acc[game.round_number].push(game);

    return acc;
  }, {});

  const sortedRounds = Object.entries(groupedGames)
  .sort((a, b) => Number(a[0]) - Number(b[0]));

// Find first round with unfinished games
  const currentRound =
  sortedRounds.find(([round, games]) =>
    games.some(
      (g) =>
        g.home_team_score === null ||
        g.away_team_score === null ||
        g.home_team_score === "" ||
        g.away_team_score === ""
    )
  )?.[0] ||
  sortedRounds[sortedRounds.length - 1]?.[0];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 6
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Typography
        variant="h4"
        sx={{
          color: "#F5F7FA",
          fontWeight: 700,
          mb: 3
        }}
      >
        ALL RESULTS
      </Typography>

      {[...sortedRounds]
        .reverse()
        .map(([round, roundGames]) => {
          const isLatestRound = round === currentRound;

          const content = (
            <Box>
              {roundGames.map((game) => {
                const gameFinished =
                  game.home_team_score !== null &&
                  game.away_team_score !== null;

                const homeWon =
                  game.home_team_score > game.away_team_score;

                const awayWon =
                  game.away_team_score > game.home_team_score;

                return (
                  <Card
                    key={game.match_number}
                    sx={{
                      mb: 2,
                      borderRadius: 4,
                      background:
                        "linear-gradient(145deg, #0B1F3A, #132D52)",
                      color: "#F5F7FA",
                      boxShadow:
                        "0 8px 20px rgba(0,0,0,0.35)"
                    }}
                  >
                    <CardContent>
                      {/* Date */}
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.7,
                          mb: 1
                        }}
                      >
                        {new Date(game.game_date).toLocaleString([], {
                          day: "numeric",
                          month: "short",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true
                        })}
                      </Typography>

                      {/* Teams */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          alignItems: "center"
                        }}
                      >
                        {/* Home */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: homeWon
                                ? 700
                                : 500
                            }}
                          >
                            {game.home_team}
                          </Typography>
                        </Box>

                        {/* Score */}
                        <Box
                          sx={{
                            px: 2,
                            textAlign: "center"
                          }}
                        >
                          {gameFinished ? (
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700
                              }}
                            >
                              {game.home_team_score} -{" "}
                              {
                                game.away_team_score
                              }
                            </Typography>
                          ) : (
                            <Chip
                              label="Upcoming"
                              sx={{
                                background:
                                  "#F9A825",
                                color: "#000",
                                fontWeight: 700
                              }}
                            />
                          )}
                        </Box>

                        {/* Away */}
                        <Box
                          sx={{
                            flex: 1,
                            textAlign: "right"
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: awayWon
                                ? 700
                                : 500
                            }}
                          >
                            {game.away_team}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider
                        sx={{
                          my: 2,
                          borderColor:
                            "rgba(255,255,255,0.1)"
                        }}
                      />

                      {/* Venue */}
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8
                        }}
                      >
                        {game.location}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          );

          // Latest round shown open
          if (isLatestRound) {
            return (
              <Box key={round} sx={{ mb: 4 }}>
                <Chip
                  label={`Current Round ${round}`}
                  sx={{
                    mb: 2,
                    background: "#1E3A5F",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 16
                  }}
                />

                {content}
              </Box>
            );
          }

          // Older rounds collapsed
          return (
            <Accordion
              key={round}
              sx={{
                mb: 2,
                background:
                  "linear-gradient(145deg, #0B1F3A, #132D52)",
                color: "#fff",
                borderRadius: "16px !important",
                overflow: "hidden",
                boxShadow:
                  "0 8px 20px rgba(0,0,0,0.35)"
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{ color: "#fff" }}
                  />
                }
              >
                <Typography
                  sx={{
                    fontWeight: 700
                  }}
                >
                  Round {round}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                {content}
              </AccordionDetails>
            </Accordion>
          );
        }
      )}
    </Box>
  );
}